import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { Brand } from "@/components/Brand";

function fmt(d) {
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || "").join(" ");
  return "Something went wrong";
}

export default function AccountAuth() {
  const { login, register } = useCustomerAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const input = "w-full bg-transparent border-0 border-b border-black/20 rounded-none px-0 py-3 text-[#0E1E2E] placeholder:text-[#8797A3] focus:border-[#22C55E] transition-colors";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      toast.success("Welcome!");
      navigate("/library");
    } catch (err) { toast.error(fmt(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F1F4F3] flex flex-col justify-center px-6 md:px-12">
      <Link to="/" className="absolute top-8 left-6 md:left-12 font-mono2 text-xs uppercase tracking-[0.2em] text-[#556673] hover:text-[#0E1E2E] flex items-center gap-2">
        <ArrowLeft size={14} /> Back to site
      </Link>
      <div className="max-w-md w-full mx-auto">
        <div className="mb-8"><Brand size={40} /></div>
        <h1 className="font-display font-black uppercase tracking-tight text-4xl md:text-5xl mb-2 text-[#0E1E2E]">
          {mode === "login" ? "Sign in" : "Create account"}
        </h1>
        <p className="text-[#556673] mb-8">Access your ebook library and more.</p>
        <form onSubmit={submit} data-testid="account-form" className="flex flex-col gap-7">
          {mode === "register" && (
            <div>
              <label className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#556673] mb-2 block">Name</label>
              <input data-testid="acct-name" className={input} value={form.name} onChange={upd("name")} placeholder="Your name" />
            </div>
          )}
          <div>
            <label className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#556673] mb-2 block">Email</label>
            <input data-testid="acct-email" type="email" className={input} value={form.email} onChange={upd("email")} placeholder="you@email.com" />
          </div>
          <div>
            <label className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#556673] mb-2 block">Password</label>
            <input data-testid="acct-password" type="password" className={input} value={form.password} onChange={upd("password")} placeholder="••••••••" />
          </div>
          <button data-testid="acct-submit" disabled={busy}
            className="px-8 py-4 bg-brand text-[#06111C] font-display font-bold uppercase tracking-wide text-sm hover:bg-[#27B95B] transition-colors disabled:opacity-50">
            {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
          </button>
        </form>
        <button data-testid="acct-toggle" onClick={() => setMode(mode === "login" ? "register" : "login")}
          className="mt-6 font-mono2 text-xs uppercase tracking-[0.15em] text-brand">
          {mode === "login" ? "New here? Create an account" : "Have an account? Sign in"}
        </button>
      </div>
    </div>
  );
}
